/*
 * FirstRunWelcome
 *
 * A quiet, once-only welcome moment for a user's very first visit to the
 * Dashboard. Introduces the two ways they can leave a "download" — speaking
 * to the mic or typing — without any pressure or gamification.
 *
 * Behavior:
 *   • Reads `selah.hasSeenWelcome.<userId>` from localStorage.
 *   • If falsy, opens a gentle dialog after a short delay (700ms) so the
 *     Dashboard's own hero has a moment to breathe first.
 *   • Sets the flag as soon as the dialog is dismissed (either "Begin"
 *     or the X).
 *   • Never shows again for that user on that device.
 *
 * Design notes:
 *   • Restful glass card, rose-gold accents matching the mic component.
 *   • Two short lines only. No emoji, no illustrations, no branding puffery.
 *   • The two CTAs mirror the two capture paths so the user can tap
 *     whichever feels right.
 *   • Fully keyboard-accessible via the underlying shadcn Dialog primitive.
 *   • data-testid on every meaningful element.
 */

import React, { useEffect, useState } from 'react';
import { Mic, PenLine, Sparkles, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const STORAGE_KEY_PREFIX = 'selah.hasSeenWelcome.';

const firstNameOf = (user) => {
  if (!user) return '';
  if (user.name && typeof user.name === 'string') {
    const trimmed = user.name.trim();
    if (trimmed) return trimmed.split(/\s+/)[0];
  }
  if (user.email && typeof user.email === 'string') {
    return user.email.split('@')[0].split('.')[0];
  }
  return '';
};

const gentleCapitalize = (name) => {
  if (!name || typeof name !== 'string') return '';
  const t = name.trim();
  if (!t) return '';
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
};

/**
 * Scroll the InAppVoiceCapture into view and focus its mic button.
 * Silent no-op if the element isn't mounted (e.g., on a different route).
 */
const focusMicButton = () => {
  const el = document.querySelector('[data-testid="voice-capture-mic-button"]');
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  // Give the scroll a beat before drawing focus, to avoid jarring jumps.
  setTimeout(() => {
    try {
      el.focus({ preventScroll: true });
    } catch (_) {
      /* ignore */
    }
  }, 350);
};

const FirstRunWelcome = () => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth() || {};
  const firstName = gentleCapitalize(firstNameOf(user));

  useEffect(() => {
    // Wait until we know who the user is; don't show for anonymous sessions.
    if (!user?.id) return undefined;

    let cancelled = false;
    const key = `${STORAGE_KEY_PREFIX}${user.id}`;

    // Safe localStorage access (SSR / private-mode / disabled storage all
    // throw; we treat any read failure as "haven't seen it yet" but never
    // *loop* the welcome — the write below will short-circuit if storage
    // is unavailable, so the user sees it at most once per session).
    let seen = false;
    try {
      seen = Boolean(window.localStorage.getItem(key));
    } catch (_) {
      seen = false;
    }
    if (seen) return undefined;

    const t = setTimeout(() => {
      if (!cancelled) setOpen(true);
    }, 700);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [user?.id]);

  const markSeen = () => {
    if (!user?.id) return;
    try {
      window.localStorage.setItem(`${STORAGE_KEY_PREFIX}${user.id}`, '1');
    } catch (_) {
      /* ignore storage errors */
    }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) markSeen();
  };

  const handleSpeak = () => {
    markSeen();
    setOpen(false);
    // Small delay so the dialog animates out before we scroll.
    setTimeout(focusMicButton, 240);
  };

  const handleType = () => {
    markSeen();
    setOpen(false);
    // Rely on the Dashboard's "Begin a Download" flow — if present, focus it.
    setTimeout(() => {
      const btn = document.querySelector('[data-testid="begin-download-button"]');
      if (btn) {
        btn.scrollIntoView({ behavior: 'smooth', block: 'center' });
        try {
          btn.focus({ preventScroll: true });
        } catch (_) {
          /* ignore */
        }
      }
    }, 240);
  };

  const greeting = firstName ? `Welcome, ${firstName}.` : 'Welcome.';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md rounded-2xl border-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(28,30,33,0.98) 0%, rgba(35,32,34,0.98) 100%)',
          border: '1px solid rgba(183,110,121,0.24)',
          boxShadow: '0 24px 60px -22px rgba(0,0,0,0.75)',
          backdropFilter: 'blur(20px)',
        }}
        data-testid="first-run-welcome-dialog"
      >
        {/* Soft ember behind the header */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-12 -right-10 w-[220px] h-[220px] rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(183,110,121,0.18) 0%, rgba(0,0,0,0) 65%)',
            filter: 'blur(20px)',
          }}
        />

        <DialogHeader className="relative">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={14} style={{ color: '#B76E79' }} />
            <span
              className="text-[10.5px] uppercase tracking-[0.22em]"
              style={{ color: 'rgba(207,199,200,0.70)' }}
              data-testid="first-run-welcome-eyebrow"
            >
              A quiet word before we start
            </span>
          </div>
          <DialogTitle
            className="text-[22px] leading-tight"
            style={{
              color: '#F2F2F2',
              fontFamily: '"Playfair Display", serif',
              letterSpacing: '-0.01em',
            }}
            data-testid="first-run-welcome-title"
          >
            {greeting}
          </DialogTitle>
          <DialogDescription
            className="text-[13.5px] leading-relaxed mt-2"
            style={{
              color: 'rgba(230,225,226,0.90)',
              fontFamily: '"Crimson Text", serif',
            }}
            data-testid="first-run-welcome-body"
          >
            Selah Downloads is a place to catch the thoughts God gives you when the world
            goes quiet. There is nothing to score, nothing to keep up with.
            When something arrives, leave it here — however feels natural in
            the moment.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 grid grid-cols-1 gap-2">
          <button
            type="button"
            onClick={handleSpeak}
            data-testid="first-run-welcome-speak-button"
            className="flex items-center gap-3 rounded-xl p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(183,110,121,0.55)] transition-colors"
            style={{
              background: 'rgba(183,110,121,0.10)',
              border: '1px solid rgba(183,110,121,0.28)',
            }}
          >
            <span
              className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
              style={{
                background: 'linear-gradient(180deg, #D7A1A8 0%, #B76E79 100%)',
                color: '#1C1C1C',
              }}
              aria-hidden
            >
              <Mic size={16} strokeWidth={2.2} />
            </span>
            <span className="flex-1 min-w-0">
              <span
                className="block text-[14px] font-semibold"
                style={{ color: '#F2F2F2' }}
              >
                Speak a download
              </span>
              <span
                className="block text-[12px] mt-0.5"
                style={{ color: 'rgba(207,199,200,0.85)' }}
              >
                Tap the mic and say it out loud. It lands in your inbox for
                review.
              </span>
            </span>
          </button>

          <button
            type="button"
            onClick={handleType}
            data-testid="first-run-welcome-type-button"
            className="flex items-center gap-3 rounded-xl p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(245,245,245,0.24)] transition-colors"
            style={{
              background: 'rgba(245,245,245,0.05)',
              border: '1px solid rgba(245,245,245,0.14)',
            }}
          >
            <span
              className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
              style={{
                background: 'rgba(245,245,245,0.08)',
                border: '1px solid rgba(245,245,245,0.18)',
                color: '#F2F2F2',
              }}
              aria-hidden
            >
              <PenLine size={16} strokeWidth={2.2} />
            </span>
            <span className="flex-1 min-w-0">
              <span
                className="block text-[14px] font-semibold"
                style={{ color: '#F2F2F2' }}
              >
                Type a download
              </span>
              <span
                className="block text-[12px] mt-0.5"
                style={{ color: 'rgba(207,199,200,0.85)' }}
              >
                Prefer to write? Use “Begin a Download” at the top of your
                Dashboard.
              </span>
            </span>
          </button>
        </div>

        <DialogFooter className="mt-4">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            data-testid="first-run-welcome-dismiss-button"
            className="text-[12.5px] w-full sm:w-auto"
            style={{
              color: 'rgba(207,199,200,0.80)',
            }}
          >
            <X size={13} className="mr-1.5" />
            Not right now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FirstRunWelcome;
